import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';

const CompanionPersonality: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [personality, setPersonality] = useState('');

  const promptSuggestions = [
    t('companionCreation.personalitySuggestion1', 'Witty and playful with a love for wordplay'),
    t('companionCreation.personalitySuggestion2', 'Calm and thoughtful, like a wise mentor'),
    t('companionCreation.personalitySuggestion3', 'Energetic and enthusiastic about everything'),
    t('companionCreation.personalitySuggestion4', 'Mysterious and poetic with deep insights'),
    t('companionCreation.personalitySuggestion5', 'Warm and nurturing with endless patience'),
    t('companionCreation.personalitySuggestion6', 'Adventurous spirit who loves exploring ideas'),
  ];

  const handleContinue = () => {
    if (personality.trim()) {
      sessionStorage.setItem('companionPersonality', personality);
      navigate('/companion/preset');
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem(
      'companionPersonality',
      t('companionCreation.defaultPersonality', 'A thoughtful and caring companion'),
    );
    navigate('/companion/preset');
  };

  const handleSuggestionClick = (suggestion: string) => {
    setPersonality(suggestion);
  };

  return (
    <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
      <div className="mobile-container flex-1 flex flex-col">
        {/* Header */}
        <div className="pt-safe px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface"
          >
            <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
          </button>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col px-6 pb-8">
          {/* Progress indicator - 6 steps, step 4 active */}
          <div className="flex space-x-2 mb-8">
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-dark-text dark:text-dark-text text-light-text mb-2">
            {t('companionCreation.personalityTitle', "Describe your ideal companion's personality")}
          </h1>
          <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-8">
            {t(
              'companionCreation.personalitySubtitle',
              'Be creative! This shapes how they interact with you',
            )}
          </p>

          {/* Textarea */}
          <div className="mb-6">
            <div className="relative">
              <textarea
                value={personality}
                onChange={(e) => setPersonality(e.target.value)}
                placeholder={t(
                  'companionCreation.personalityPlaceholder',
                  'E.g., A curious explorer with a gentle soul, always ready with encouragement and deep questions...',
                )}
                className="w-full px-4 py-3 bg-light-surface dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-white placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[160px] resize-y"
                maxLength={5000}
              />
              <SparklesIcon className="absolute top-3 right-3 w-5 h-5 text-primary-500 pointer-events-none" />
            </div>
            <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mt-2">
              {t('companionCreation.characterCount5000', '{{count}}/5,000 characters', {
                count: personality.length,
              })}
            </p>
          </div>

          {/* Inspiration prompts */}
          <div className="mb-auto">
            <p className="text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-3">
              {t('companionCreation.needInspiration', 'Need inspiration? Try these:')}
            </p>
            <div className="space-y-2">
              {promptSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full text-left p-3 bg-dark-surface dark:bg-dark-surface bg-light-surface rounded-xl text-sm text-dark-text dark:text-dark-text text-light-text hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-200 transition-colors"
                >
                  <span className="text-primary-500 mr-2">✨</span>
                  {suggestion}
                </button>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleContinue}
              disabled={!personality.trim()}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.continue', 'Continue')}
            </button>
            <button onClick={handleSkip} className="btn-secondary">
              {t('companionCreation.skipForNow', 'Skip for now')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanionPersonality;
